import { computed, ref, type ComputedRef, type Ref } from "vue"
import type { MediaFormData } from "../media/media"
import type { MediaUpdateInput } from "../graphql/types"
import { computePerItemArray, type ArrayField, type ArrayMode } from "../media/batch/batchEditItem"

export type UUID = string

export interface BatchResult {
  succeeded: UUID[]
  failed: Array<{ id: UUID; error: Error }>
}

export type Mode = "empty" | "single" | "batch"

export interface CommonFieldsResult {
  common: Map<keyof MediaFormData, unknown>
  mixed: Set<keyof MediaFormData>
}

export interface UploadEditSession {
  selectedIds: Ref<Set<UUID>>
  formDrafts: Ref<Map<UUID, MediaFormData>>
  activeId: Ref<UUID | null>
  mode: ComputedRef<Mode>
  hasDirty: ComputedRef<boolean>

  ensureDraft: (id: UUID, initial: MediaFormData) => MediaFormData
  patchDraft: (id: UUID, patch: Partial<MediaFormData>) => void
  discardDraft: (id: UUID) => void

  applyFromSource: (targetId: UUID, sourceId: UUID) => void
  commonFields: (ids: UUID[]) => CommonFieldsResult
  applyBatch: (
    ids: UUID[],
    patch: Partial<MediaFormData>,
    arrayModes?: Partial<Record<ArrayField, ArrayMode>>
  ) => Promise<BatchResult>
  saveSingle: (id: UUID) => Promise<void>
  saveSingleUpdate: (id: UUID, update: MediaUpdateInput) => Promise<void>
}

export interface UploadEditSessionOptions {
  /** Persists a single draft to the server and returns the canonical MediaFormData after save. */
  saveDraft: (id: UUID, draft: MediaFormData) => Promise<MediaFormData | void>
  /**
   * Persists a ready-made MediaUpdateInput to the server. Used by batch edit,
   * whose array fields are already resolved to uuid[] — round-tripping them back
   * through full entity objects would drop relations the draft did not yet have.
   */
  saveUpdate?: (id: UUID, update: MediaUpdateInput) => Promise<void>
}

const SCALAR_FIELDS: Array<keyof MediaFormData> = [
  "copyright",
  "copyrightUrl",
  "license",
  "licenseUrl",
  "sourceUrl",
  "watermark"
]

const SET_FIELDS: Array<keyof MediaFormData> = [
  "tags",
  "companies",
  "gameSeries",
  "games",
  "gameConsoles"
]

function sameSetByUuid(a: unknown, b: unknown): boolean {
  if (!Array.isArray(a) || !Array.isArray(b)) return false
  if (a.length !== b.length) return false
  const aIds = new Set(a.map((x: { uuid: string }) => x.uuid))
  for (const item of b as Array<{ uuid: string }>) {
    if (!aIds.has(item.uuid)) return false
  }
  return true
}

function unionByUuid<T extends { uuid: string }>(a: T[], b: T[]): T[] {
  const seen = new Set<string>()
  const result: T[] = []
  for (const item of [...a, ...b]) {
    if (!seen.has(item.uuid)) {
      seen.add(item.uuid)
      result.push(item)
    }
  }
  return result
}

function buildPatchedDraft(
  current: MediaFormData,
  patch: Partial<MediaFormData>,
  arrayModes?: Partial<Record<ArrayField, ArrayMode>>
): MediaFormData {
  const next: MediaFormData = { ...current, ...patch }

  for (const field of SET_FIELDS) {
    const patchValue = (patch as Record<string, unknown>)[field]
    if (patchValue === undefined) continue
    const currentList = current[field] as Array<{ uuid: string }>
    const patchList = patchValue as Array<{ uuid: string }>
    const mode = arrayModes?.[field as ArrayField] ?? "add"
    ;(next[field] as unknown) = computePerItemArray(mode, currentList, patchList)
  }

  return next
}

export function createUploadEditSession(options: UploadEditSessionOptions): UploadEditSession {
  const selectedIds = ref<Set<UUID>>(new Set())
  const formDrafts = ref<Map<UUID, MediaFormData>>(new Map())
  const activeId = ref<UUID | null>(null)

  const mode = computed<Mode>(() => {
    const size = selectedIds.value.size
    if (size >= 2) return "batch"
    if (size === 1 && activeId.value) return "single"
    return "empty"
  })

  const hasDirty = computed(() => formDrafts.value.size > 0)

  function ensureDraft(id: UUID, initial: MediaFormData): MediaFormData {
    const existing = formDrafts.value.get(id)
    if (existing) return existing
    const draft: MediaFormData = { ...initial }
    const next = new Map(formDrafts.value)
    next.set(id, draft)
    formDrafts.value = next
    return draft
  }

  function patchDraft(id: UUID, patch: Partial<MediaFormData>): void {
    const current = formDrafts.value.get(id)
    if (!current) return
    const next = new Map(formDrafts.value)
    next.set(id, { ...current, ...patch })
    formDrafts.value = next
  }

  function discardDraft(id: UUID): void {
    if (!formDrafts.value.has(id)) return
    const next = new Map(formDrafts.value)
    next.delete(id)
    formDrafts.value = next
  }

  function applyFromSource(targetId: UUID, sourceId: UUID): void {
    const source = formDrafts.value.get(sourceId)
    const target = formDrafts.value.get(targetId)
    if (!source || !target) return

    const updated: MediaFormData = { ...target }

    for (const field of SCALAR_FIELDS) {
      ;(updated[field] as unknown) = source[field]
    }

    for (const field of SET_FIELDS) {
      const srcValue = source[field] as Array<{ uuid: string }>
      const tgtValue = target[field] as Array<{ uuid: string }>
      ;(updated[field] as unknown) = unionByUuid(tgtValue, srcValue)
    }

    const next = new Map(formDrafts.value)
    next.set(targetId, updated)
    formDrafts.value = next
  }

  function commonFields(ids: UUID[]): CommonFieldsResult {
    const common = new Map<keyof MediaFormData, unknown>()
    const mixed = new Set<keyof MediaFormData>()

    const drafts = ids
      .map(id => formDrafts.value.get(id))
      .filter((d): d is MediaFormData => !!d)

    if (drafts.length === 0) return { common, mixed }

    const first = drafts[0]!

    for (const field of SCALAR_FIELDS) {
      const firstValue = first[field]
      const allEqual = drafts.every(d => d[field] === firstValue)
      if (allEqual) {
        common.set(field, firstValue)
      } else {
        mixed.add(field)
      }
    }

    for (const field of SET_FIELDS) {
      const firstValue = first[field]
      const allEqual = drafts.every(d => sameSetByUuid(d[field], firstValue))
      if (allEqual) {
        common.set(field, firstValue)
      } else {
        mixed.add(field)
      }
    }

    return { common, mixed }
  }

  async function applyBatch(
    ids: UUID[],
    patch: Partial<MediaFormData>,
    arrayModes?: Partial<Record<ArrayField, ArrayMode>>
  ): Promise<BatchResult> {
    const succeeded: UUID[] = []
    const failed: Array<{ id: UUID; error: Error }> = []

    const results = await Promise.allSettled(
      ids.map(async (id) => {
        const current = formDrafts.value.get(id)
        if (!current) throw new Error(`no draft for ${id}`)

        const updated = buildPatchedDraft(current, patch, arrayModes)
        // Update the draft in-session before saving so other reactive views see the patch.
        const next = new Map(formDrafts.value)
        next.set(id, updated)
        formDrafts.value = next

        await options.saveDraft(id, updated)
        return id
      })
    )

    for (let i = 0; i < results.length; i++) {
      const r = results[i]!
      const id = ids[i]!
      if (r.status === "fulfilled") {
        succeeded.push(id)
        // Clear draft on success.
        if (formDrafts.value.has(id)) {
          const next = new Map(formDrafts.value)
          next.delete(id)
          formDrafts.value = next
        }
      } else {
        const reason = (r as PromiseRejectedResult).reason
        failed.push({ id, error: reason instanceof Error ? reason : new Error(String(reason)) })
      }
    }

    return { succeeded, failed }
  }

  async function saveSingle(id: UUID): Promise<void> {
    const draft = formDrafts.value.get(id)
    if (!draft) return
    await options.saveDraft(id, draft)
    discardDraft(id)
  }

  async function saveSingleUpdate(id: UUID, update: MediaUpdateInput): Promise<void> {
    if (!options.saveUpdate) throw new Error("saveUpdate not configured")
    await options.saveUpdate(id, update)
    discardDraft(id)
  }

  return {
    selectedIds,
    formDrafts,
    activeId,
    mode,
    hasDirty,
    ensureDraft,
    patchDraft,
    discardDraft,
    applyFromSource,
    commonFields,
    applyBatch,
    saveSingle,
    saveSingleUpdate,
  }
}
